/**
 * Command Center — Stability widget.
 *
 * Surfaces system component health from the snapshot's `stability` quadrant
 * (system_monitoring_api.get_system_health). Colors strictly off the backend's
 * OWN status fields (overall `status` + per-component `status`) — no invented
 * thresholds — plus warning/error counts. Read-only; off the execution path.
 */

import React from 'react';
import { ServerCog } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import type { CommandCenterQuadrant } from '../../lib/api';

interface ComponentHealth {
  status?: string;
  error?: string;
}

type BadgeVariant = 'success' | 'warning' | 'destructive' | 'secondary';

const OVERALL_VARIANT: Record<string, BadgeVariant> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'destructive',
};

// Per-component status -> dot color, keyed on the strings get_system_health emits.
const DOT: Record<string, string> = {
  healthy: '#1FC78F', connected: '#1FC78F', running: '#1FC78F', ok: '#1FC78F', configured: '#1FC78F',
  degraded: '#FFCC00', stopped: '#FFCC00',
  unhealthy: '#E5484D', error: '#E5484D',
};
const dotColor = (s?: string): string => DOT[(s || '').toLowerCase()] || '#6b7280';

const prettify = (k: string): string => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const StabilityWidget: React.FC<{ data: CommandCenterQuadrant | undefined }> = ({ data }) => {
  const errored = !!data && typeof data.error === 'string';
  const q = (data as Record<string, unknown> | undefined) ?? {};
  const overall = String((q.status as string) || (errored ? 'unhealthy' : 'unknown'));
  const components = (q.components as Record<string, ComponentHealth> | undefined) ?? {};
  const warnings = (q.warnings as unknown[] | undefined) ?? [];
  const errors = (q.errors as unknown[] | undefined) ?? [];
  const entries = Object.entries(components);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <ServerCog className="h-4 w-4" /> Stability
        </CardTitle>
        <Badge variant={errored ? 'destructive' : (OVERALL_VARIANT[overall] || 'secondary')}>
          {errored ? 'degraded' : overall}
        </Badge>
      </CardHeader>
      <CardContent className="flex-1">
        {errored ? (
          <div className="text-sm text-destructive">{String(data?.error)}</div>
        ) : entries.length === 0 ? (
          <div className="text-sm text-muted-foreground">No component data.</div>
        ) : (
          <div className="space-y-1.5">
            {entries.map(([name, c]) => (
              <div key={name} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: dotColor(c?.status) }} />
                  {prettify(name)}
                </span>
                <span className="text-xs text-muted-foreground">{c?.status || 'unknown'}</span>
              </div>
            ))}
            {(warnings.length > 0 || errors.length > 0) && (
              <div className="mt-2 flex gap-3 text-xs">
                {errors.length > 0 && <span className="text-destructive">{errors.length} error(s)</span>}
                {warnings.length > 0 && <span className="text-amber-500">{warnings.length} warning(s)</span>}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StabilityWidget;
